// Hook personalizado para verificar si el usuario actual ha sido bloqueado.
// Escucha cambios en la tabla unlinked en tiempo real.
// Retorna el estado de bloqueo para que el componente maneje la UI.
// Adaptado para web desde amarsi-app.

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export function useBlockDetection(friendId) {
  const [isBlocked, setIsBlocked] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const { user } = useAuth();

  // Función para verificar si el usuario actual ha sido bloqueado por friendId
  const checkIfBlocked = useCallback(async () => {
    console.log('useBlockDetection: checkIfBlocked iniciado');
    console.log('- user.id:', user?.id);
    console.log('- friendId:', friendId);
    
    if (!user?.id || !friendId) {
      console.log('useBlockDetection: Faltan datos, saliendo');
      setIsChecking(false);
      return;
    }

    try {
      console.log('useBlockDetection: Verificando si usuario ha sido bloqueado por:', friendId);
      
      const { data, error } = await supabase
        .from('unlinked')
        .select('*')
        .eq('gestor', friendId) // friendId es quien gestiona el bloqueo
        .eq('bloqueado', user.id) // usuario actual es el bloqueado
        .maybeSingle();

      console.log('useBlockDetection: Resultado query:', { data, error });

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error('useBlockDetection: Error verificando bloqueo:', error);
        return;
      }

      const blocked = !!data;
      console.log('useBlockDetection: Resultado verificación bloqueo:', blocked);
      setIsBlocked(blocked);
    } catch (error) {
      console.error('useBlockDetection: Error en verificación de bloqueo:', error);
    } finally {
      setIsChecking(false);
    }
  }, [user?.id, friendId]);

  // Efecto para verificar bloqueo inicial y configurar suscripción a tiempo real
  useEffect(() => {
    if (!user?.id || !friendId) {
      console.log('useBlockDetection: Esperando a que usuario esté disponible');
      setIsChecking(false);
      return;
    }

    console.log('useBlockDetection: Iniciando detección de bloqueo para friendId:', friendId);

    // Verificación inicial
    checkIfBlocked();

    // Configurar suscripción a tiempo real para cambios en la tabla unlinked
    // Escucha tanto INSERT como DELETE para manejar todos los cambios
    const channelName = `block_detection_${user.id}_${friendId}`;
    console.log('useBlockDetection: Creando canal:', channelName);
    
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'unlinked'
        }, 
        (payload) => {
          console.log('useBlockDetection: INSERT detectado en unlinked:', payload);
          
          // Verificar si este cambio es relevante para nuestro usuario
          const { gestor, bloqueado } = payload.new;
          if (gestor === friendId && bloqueado === user.id) {
            console.log('useBlockDetection: ¡Usuario bloqueado por friend_id en tiempo real!');
            setIsBlocked(true);
          }
        }
      )
      .on('postgres_changes', 
        { 
          event: 'DELETE', 
          schema: 'public', 
          table: 'unlinked'
        }, 
        (payload) => {
          console.log('useBlockDetection: DELETE detectado en unlinked:', payload);
          
          // Verificar si este cambio es relevante para nuestro usuario
          const { gestor, bloqueado } = payload.old;
          if (gestor === friendId && bloqueado === user.id) {
            console.log('useBlockDetection: ¡Bloqueo eliminado por friend_id!');
            setIsBlocked(false);
          }
        }
      )
      .subscribe((status) => {
        console.log('useBlockDetection: Status del canal:', status);
        if (status === 'SUBSCRIBED') {
          console.log('useBlockDetection: ✅ Suscripción activa para detección de bloqueos');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('useBlockDetection: ❌ Error en la suscripción');
        }
      });

    // Cleanup
    return () => {
      console.log('useBlockDetection: Limpiando canal de detección de bloqueo');
      supabase.removeChannel(channel);
    };
  }, [user?.id, friendId, checkIfBlocked]);

  return {
    isBlocked,
    isChecking
  };
}
